import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import { forwardRef, useState } from "react";
import {
  TbBook,
  TbChevronDown,
  TbHelpCircle,
  TbLogout,
  TbMenu2,
  TbX,
} from "react-icons/tb";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useMediaQuery } from "usehooks-ts";
import { reset } from "../../../app/reducers/BookMemorySlice";
import { Body } from "../../../styles/typography";

const ImageSection = styled.div`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 1050;
  background: white;
  border-bottom: 2px solid rgb(${(props) => props.theme.colors.neutral600});
  padding: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Image = styled.img`
  object-fit: contain;
  max-height: 48px;
  cursor: pointer;
  @media screen and (max-width: 400px) {
    max-height: 40px;
  }
`;

const RightSection = styled.div`
  display: flex;
  gap: 32px;
  align-items: center;
`;

const Divider = styled.div`
  background-color: rgb(${(props) => props.theme.colors.neutral100});
  width: 2px;
  height: 40px;
`;

const Logout = styled(Body)`
  cursor: pointer;
`;

const TriggerContainer = styled.div`
  background-color: transparent;
  cursor: pointer;
  display: flex;
  border-radius: 8px;
  padding: 12px 8px;
  align-items: center;
  gap: 8px;
  @media (hover: hover) {
    :hover {
      background-color: rgba(226, 228, 233, 0.5);
    }
  }
  &[data-state="open"] {
    background-color: rgba(226, 228, 233, 1);
    svg {
      rotate: 180deg;
    }
  }
  transition:
    background-color 200ms,
    rotate 200ms;
  svg {
    min-height: 24px;
    min-width: 24px;
    transition: rotate 200ms;
  }
`;

const StyledLink = styled(Link)`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 12px 8px;
  border-radius: 8px;
  text-decoration: none;
  ${Body} {
    color: rgb(${(props) => props.theme.colors.neutral200});
  }
  svg {
    min-height: 20px;
    min-width: 20px;
    color: rgb(${(props) => props.theme.colors.neutral200});
  }
  @media (hover: hover) {
    :hover {
      background-color: rgba(226, 228, 233, 0.5);
    }
  }
`;

const Hamburger = styled.div`
  width: 40px;
  height: 40px;
  background-color: rgb(${(props) => props.theme.colors.offWhite});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  border-radius: 8px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  align-items: center;
  svg {
    min-width: 20px;
    min-height: 20px;
  }
`;

const PopoverContent = styled(motion.div)`
  padding: 8px;
  background-color: rgb(${(props) => props.theme.colors.white});
  border: 2px solid rgb(${(props) => props.theme.colors.neutral150});
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;
  box-shadow:
    0px 8px 16px rgba(11, 12, 15, 0.07),
    0px 4px 8px rgba(11, 12, 15, 0.07),
    0px 0px 4px rgba(11, 12, 15, 0.07);
  max-height: 300px;
  overflow-y: auto;
`;

const DesktopTrigger = forwardRef((props, ref) => (
  <TriggerContainer ref={ref} {...props}>
    <Body>Resources</Body>
    <TbChevronDown />
  </TriggerContainer>
));

const MobileTrigger = forwardRef(({ open, ...props }, ref) => (
  <Hamburger ref={ref} {...props}>
    <AnimatePresence initial={false}>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ display: "flex" }}
        >
          <TbX />
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ display: "flex" }}
        >
          <TbMenu2 />
        </motion.div>
      )}
    </AnimatePresence>
  </Hamburger>
));

const DropDownItems = ({ isMobile }) => (
  <>
    <StyledLink
      as="a"
      href="https://www.mystoriesmatter.com/memory-book?no-redirect=true"
      target="_blank"
      rel="noreferrer noopener"
    >
      <TbBook />
      <Body>View a book sample</Body>
    </StyledLink>

    <StyledLink
      href="#"
      onClick={() => {
        window.Calendly?.initPopupWidget({
          url: "https://calendly.com/mystoriesmatter-call/memoir-call?hide_gdpr_banner=1",
        });
        return false;
      }}
    >
      <TbHelpCircle />
      <Body>Help</Body>
    </StyledLink>
    {isMobile && (
      <StyledLink to="/logout">
        <TbLogout />
        <Body>Log out</Body>
      </StyledLink>
    )}
  </>
);

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 800px)");
  const [open, setOpen] = useState(false);

  return (
    <ImageSection>
      <Image
        onClick={() => {
          dispatch(reset());
          navigate("/");
        }}
        src="/logo.png"
      />
      <RightSection>
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>
            {isMobile ? <MobileTrigger open={open} /> : <DesktopTrigger />}
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              side="bottom"
              align={isMobile ? "end" : "start"}
              sideOffset={8}
              style={{ zIndex: 1100 }}
            >
              <PopoverContent
                animate={{ scale: 1, opacity: 1 }}
                initial={{ scale: 0.95, opacity: 0 }}
                transition={{ duration: 0.1 }}
              >
                <DropDownItems isMobile={isMobile} />
              </PopoverContent>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        <AnimatePresence initial={false}>
          {!isMobile && (
            <motion.div
              style={{ display: "flex", gap: "32px", alignItems: "center" }}
              animate={{ opacity: 1 }}
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
            >
              <Divider />
              <Logout as={Link} to="/logout">
                Log out
              </Logout>
            </motion.div>
          )}
        </AnimatePresence>
      </RightSection>
    </ImageSection>
  );
};

export default Navbar;
